/**
 * TRIL-680: render a category code as a human label.
 *
 * Categories are stored as uppercase codes (`POLITICS`, `URBAN_PLANNING`). Three surfaces show
 * them to readers — the server feed's "Trending in X" reason (getUserFeed.query.ts), the
 * sidebar's TrendingItem label (trending.repository.ts), and the client's empty-topic-feed
 * heading (topicEmptyState.ts) — and each had its own copy of
 * `charAt(0).toUpperCase() + slice(1).toLowerCase()`, which is right for one underscored word
 * and wrong for the rest: `URBAN_PLANNING` rendered as "Urban_planning" everywhere.
 *
 * One helper, three callers (two server, one client), shared via `@trilirivos/story-text` so
 * they cannot drift from each other, and so a client-side fix for an acronym never disagrees
 * with the server's. Same fix shape as TRIL-661's defect 4.
 *
 * Splitting ONLY on `_` was also wrong for a stored value that is already space-separated and
 * title-cased (`Social Issues` -> the old code produced "Social issues", destroying the correct
 * casing of the second word) — this helper splits on underscores AND whitespace so every token
 * gets evaluated independently.
 *
 * Acronyms (`AI`, `ESA`, `IOT`, `NASA`, ...) must stay uppercase, but a shape-based heuristic
 * ("short and all-caps -> keep uppercase") is a trap: `client/src/enums/interests.enum.ts` also
 * has `ART`, `FILM`, `LAW`, `WAR` — short all-caps values that are ordinary WORDS, not acronyms.
 * A heuristic can't tell them apart, so this is an explicit, documented set instead. Add new
 * acronyms here as they're introduced as interest categories.
 */
const ACRONYMS = new Set(["AI", "ESA", "IOT", "NASA"]);

export function formatTopicLabel(category: string): string {
	return category
		.trim()
		.split(/[_\s]+/)
		.map((word) => word.trim())
		.filter(Boolean)
		.map((word) => {
			const upper = word.toUpperCase();
			if (ACRONYMS.has(upper)) return upper;
			return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
		})
		.join(" ");
}
